import { BrowserRouter } from 'react-router-dom'
import { lazy, Suspense, useEffect, useState } from 'react'

import type { AccessGateway } from '../domain/access'
import { SessionGuard } from '../features/access/SessionGuard'
import { PwaProvider } from '../features/install/PwaProvider'
import { PwaStatus } from '../features/install/PwaStatus'
import { getImmediateRuntimeAccessGateway, loadRuntimeAccessGateway } from '../services/syncRuntime'
import { AppRouter } from './router'
import { ToastProvider } from '../components/ui/Toast'

const CelebrationLayer = lazy(() => import('../components/celebration/CelebrationLayer')
  .then((module) => ({ default: module.CelebrationLayer })))

export function App({ gateway }: { gateway?: AccessGateway }) {
  const [loadedGateway, setLoadedGateway] = useState<AccessGateway | null>(() => getImmediateRuntimeAccessGateway())
  const [runtimeError, setRuntimeError] = useState<string | null>(null)
  const activeGateway = gateway ?? loadedGateway

  useEffect(() => {
    if (gateway) return
    let active = true
    void loadRuntimeAccessGateway()
      .then((loaded) => { if (active) setLoadedGateway(loaded) })
      .catch((error: unknown) => {
        if (active) setRuntimeError(error instanceof Error ? error.message : 'Runtime indisponible.')
      })
    return () => {
      active = false
    }
  }, [gateway])

  if (runtimeError) {
    return <main className="app-loading" role="alert">{runtimeError}</main>
  }

  if (!activeGateway) {
    return (
      <main className="app-loading" aria-live="polite">
        Ouverture de Caravan…
      </main>
    )
  }

  return (
    <BrowserRouter>
      <PwaProvider>
        <ToastProvider>
          <SessionGuard gateway={activeGateway}>
            <AppRouter gateway={activeGateway} />
          </SessionGuard>
          <PwaStatus />
          <Suspense fallback={null}><CelebrationLayer /></Suspense>
        </ToastProvider>
      </PwaProvider>
    </BrowserRouter>
  )
}
